import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Users,
  FileText,
  FolderKanban,
  Quote,
  Newspaper,
  MessageSquare,
  Heart,
  Eye,
  Bell,
  UserPlus,
  Mail,
} from "lucide-react";
import { API } from "./_apiConfig";

const DashboardHome = () => {
  const { token, user } = useAuth();
  const [stats, setStats] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
    if (user?.role === "admin") {
      fetchNotifications();
    }
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      const response = await fetch(`${API}/admin/notifications`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setNotifications(data);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  const statCards = [
    { title: "Utilisateurs", value: stats?.total_users || 0, icon: Users, color: "text-foreground" },
    { title: "Articles", value: stats?.total_articles || 0, icon: FileText, color: "text-foreground" },
    { title: "Projets", value: stats?.total_projects || 0, icon: FolderKanban, color: "text-foreground" },
    { title: "Témoignages", value: stats?.total_testimonials || 0, icon: Quote, color: "text-foreground" },
    { title: "Abonnés", value: stats?.total_subscribers || 0, icon: Newspaper, color: "text-foreground" },
    { title: "Commentaires", value: stats?.total_comments || 0, icon: MessageSquare, color: "text-foreground" },
    { title: "Likes", value: stats?.total_likes || 0, icon: Heart, color: "text-foreground" },
    { title: "Vues Totales", value: stats?.total_views || 0, icon: Eye, color: "text-foreground" },
  ];

  return (
    <div data-testid="admin-dashboard-home">
      <h2 className="font-heading text-xl sm:text-2xl font-bold text-foreground mb-4 sm:mb-6">
        Tableau de Bord
      </h2>

      {/* Notifications Section */}
      {user?.role === "admin" && notifications.length > 0 && (
        <Card className="mb-6" data-testid="notifications-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Notifications ({notifications.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {notifications.slice(0, 5).map((notif) => (
              <div
                key={notif.id}
                className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 border border-border"
              >
                {notif.type === "application" && <UserPlus className="h-4 w-4 text-foreground" />}
                {notif.type === "contact" && <Mail className="h-4 w-4 text-foreground" />}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate">{notif.title}</p>
                  <p className="text-xs text-muted-foreground">
                    {new Date(notif.created_at).toLocaleDateString("fr-FR")}
                  </p>
                </div>
                <Link
                  to={notif.type === "application" ? "/admin/applications" : "/admin/contacts"}
                  className="text-xs text-foreground hover:underline"
                >
                  Voir
                </Link>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4">
        {statCards.map((stat) => (
          <Card key={stat.title}>
            <CardContent className="p-3 sm:pt-6 sm:px-6">
              <div className="flex flex-col sm:flex-row items-center sm:items-center gap-2 sm:gap-4 text-center sm:text-left">
                <div className={`p-2 sm:p-3 rounded-lg sm:rounded-xl bg-secondary ${stat.color}`}>
                  <stat.icon className="h-4 w-4 sm:h-6 sm:w-6" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-muted-foreground">{stat.title}</p>
                  <p className="text-lg sm:text-2xl font-bold text-foreground">{stat.value}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default DashboardHome;
